import React from 'react';
import { CalculationSummary } from '../../utils/calculationUtils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

interface SummarySectionProps {
  summary: CalculationSummary;
  isCalculating: boolean;
}

const SummarySection: React.FC<SummarySectionProps> = ({ 
  summary, 
  isCalculating 
}) => {
  const formatCurrency = (value: number | undefined) => {
    if (value === undefined) return "Calculating...";
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(value);
  };
  
  const formatPercentage = (value: number | undefined) => {
    if (value === undefined) return "N/A";
    return value.toFixed(2) + "%";
  };
  
  return (
    <div className="p-6 space-y-6">
      {/* Quick Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200 text-center">
          <div className="flex items-center mb-2 justify-center">
            <span className="text-blue-600 -ml-[9px] text-[70%]">DISBURSEMENT</span>
          </div>
          <div className="font-bold text-blue-900 text-[14px] text-center">
            {isCalculating ? (
              <span className="text-gray-400">Calculating...</span>
            ) : (
              formatCurrency(summary.disbursementAmount)
            )}
          </div>
        </div>
        
        <div className={`bg-gradient-to-br p-4 rounded-xl border ${
          summary.isNegativeMargin 
            ? 'from-red-50 to-red-100 border-red-200' 
            : 'from-emerald-50 to-emerald-100 border-emerald-200'
        }`}>
          <div className="flex items-center justify-center mb-2">
            <span className={`text-[10px] font-medium ${
              summary.isNegativeMargin ? 'text-red-600' : 'text-emerald-600'
            }`}>
              MARGIN %
            </span>
          </div>
          <div className={`text-[14px] font-bold text-center ${
            summary.isNegativeMargin ? 'text-red-900' : 'text-emerald-900'
          }`}>
            {isCalculating ? (
              <span className="text-gray-400">Calculating...</span>
            ) : (
              formatPercentage(summary.marginPercentage)
            )}
          </div>
        </div>
      </div>

      {/* Detailed Financial Breakdown */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b">
          <h3 className="text-sm font-semibold text-gray-900">Financial Breakdown</h3>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold text-gray-900">Component</TableHead>
                <TableHead className="text-right font-semibold text-gray-900">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="hover:bg-gray-50">
                <TableCell className="font-medium">Total Cost (A)</TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(summary.totalCost)}</TableCell>
              </TableRow>
              <TableRow className="hover:bg-gray-50">
                <TableCell className="font-medium pl-6 text-gray-600">GST @ {(summary.gstAmount / summary.baseRental * 100).toFixed(0)}%</TableCell>
                <TableCell className="text-right font-mono text-gray-600">{formatCurrency(summary.gstAmount)}</TableCell>
              </TableRow>
              <TableRow className="hover:bg-gray-50">
                <TableCell className="font-medium pl-6 text-gray-600">Base Rental</TableCell>
                <TableCell className="text-right font-mono text-gray-600">{formatCurrency(summary.baseRental)}</TableCell>
              </TableRow>
              <TableRow className="hover:bg-blue-50 bg-blue-25">
                <TableCell className="font-semibold text-blue-900">Amount Disbursed (B)</TableCell>
                <TableCell className="text-right font-mono font-semibold text-blue-900">{formatCurrency(summary.disbursementAmount)}</TableCell>
              </TableRow>
              <TableRow className="hover:bg-gray-50">
                <TableCell className="font-medium">Security Deposit (C)</TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(summary.securityDeposit)}</TableCell>
              </TableRow>
              <TableRow className="hover:bg-gray-50">
                <TableCell className="font-medium">Processing Fee (D)</TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(summary.processingFee)}</TableCell>
              </TableRow>
              {summary.advanceRentalEnabled && (
                <>
                  <TableRow className="hover:bg-purple-50 bg-purple-25">
                    <TableCell className="font-medium text-purple-900 pl-6">Advance Rentals</TableCell>
                    <TableCell className="text-right font-mono text-purple-900">{formatCurrency(summary.advanceRentals)}</TableCell>
                  </TableRow>
                  <TableRow className="hover:bg-purple-50 bg-purple-25">
                    <TableCell className="font-medium text-purple-900 pl-6">GST on Advance Rentals</TableCell>
                    <TableCell className="text-right font-mono text-purple-900">{formatCurrency(summary.gstOnAdvanceRentals)}</TableCell>
                  </TableRow>
                  <TableRow className="hover:bg-purple-50 bg-purple-25">
                    <TableCell className="font-semibold text-purple-900">Total Advance Rental Incl. GST (E)</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-purple-900">{formatCurrency(summary.totalAdvanceRentalInclGst)}</TableCell>
                  </TableRow>
                </>
              )}
              <TableRow className="hover:bg-blue-50 bg-blue-25">
                <TableCell className="font-semibold text-blue-900">
                  Total Capital Available ({summary.advanceRentalEnabled ? 'F = B + C + D + E' : 'F = B + C + D'})
                </TableCell>
                <TableCell className="text-right font-mono font-semibold text-blue-900">{formatCurrency(summary.totalCapitalAvailable)}</TableCell>
              </TableRow>
              <TableRow className="hover:bg-gray-50">
                <TableCell className="font-medium">
                  Cash in Hand ({summary.advanceRentalEnabled ? 'G = F − A − Adv. Rental TDS' : 'G = F − A'})
                </TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(summary.cashInHand)}</TableCell>
              </TableRow>
              <TableRow className="hover:bg-gray-50">
                <TableCell className="font-medium">Cash Flow to Astrafin (H)</TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(summary.cashFlowToAstrafin)}</TableCell>
              </TableRow>
              <TableRow className="hover:bg-gray-50">
                <TableCell className="font-medium">TDS on H (I)</TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(summary.tdsOnCashFlow)}</TableCell>
              </TableRow>
              <TableRow className="hover:bg-gray-50">
                <TableCell className="font-medium">Effective Cash Flow (J = H − I)</TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(summary.effectiveCashFlow)}</TableCell>
              </TableRow>
              <TableRow className="hover:bg-gray-50">
                <TableCell className="font-medium">Refundable Security (K)</TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(summary.refundableSecurity)}</TableCell>
              </TableRow>
              <TableRow className="hover:bg-gray-50">
                <TableCell className="font-medium">
                  GST Payable after ITC Setoff ({summary.advanceRentalEnabled ? 'L = H − GST(A) + GST(Adv.)' : 'L = H − GST(A)'})
                </TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(summary.gstPayableAfterSetoff)}</TableCell>
              </TableRow>
              <TableRow className="hover:bg-blue-50 bg-blue-25">
                <TableCell className="font-semibold text-blue-900">Cash Available (M = G + J)</TableCell>
                <TableCell className="text-right font-mono font-semibold text-blue-900">{formatCurrency(summary.cashAvailable)}</TableCell>
              </TableRow>
              <TableRow className="hover:bg-orange-50 bg-orange-25">
                <TableCell className="font-semibold text-orange-900">Expenses Left (N = K + L)</TableCell>
                <TableCell className="text-right font-mono font-semibold text-orange-900">{formatCurrency(summary.expensesLeft)}</TableCell>
              </TableRow>
              <TableRow className={`hover:${summary.isNegativeMargin ? 'bg-red-50' : 'bg-emerald-50'} ${summary.isNegativeMargin ? 'bg-red-25' : 'bg-emerald-25'}`}>
                <TableCell className={`font-semibold ${summary.isNegativeMargin ? 'text-red-900' : 'text-emerald-900'}`}>
                  Margin Available (O = M − N)
                </TableCell>
                <TableCell className={`text-right font-mono font-semibold ${summary.isNegativeMargin ? 'text-red-900' : 'text-emerald-900'}`}>
                  {formatCurrency(summary.marginAvailable)}
                </TableCell>
              </TableRow>
              <TableRow className={`hover:${summary.isNegativeMargin ? 'bg-red-50' : 'bg-emerald-50'} ${summary.isNegativeMargin ? 'bg-red-25' : 'bg-emerald-25'}`}>
                <TableCell className={`font-semibold ${summary.isNegativeMargin ? 'text-red-900' : 'text-emerald-900'}`}>
                  Margin %
                </TableCell>
                <TableCell className={`text-right font-mono font-semibold ${summary.isNegativeMargin ? 'text-red-900' : 'text-emerald-900'}`}>
                  {formatPercentage(summary.marginPercentage)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default SummarySection;